/**
 * TenantInventoryTab — SAAS-COMPANIES-P2-INVENTORY-TAB.
 *
 * Aba "Estoque" do detalhe da empresa. Reaproveita listInventory({tenant_id})
 * (endpoint /api/admin/inventory com a ternária platform-only). READ-ONLY: só
 * contadores por status + lista (qr / status / modelo / técnico / device).
 * Sem ações destrutivas nesta fase.
 */
import { useEffect, useMemo, useState } from "react";
import { listInventory } from "../../lib/api.js";

const STATUS_LABEL = {
  stock: "Em estoque", com_tecnico: "Com técnico", reserved: "Reservado",
  installed: "Instalado", devolvido: "Devolvido", defeito: "Defeito",
  lost: "Perdido", rma: "RMA", maintenance: "Manutenção",
};
const STATUS_TONE = {
  stock: "text-noc-muted", com_tecnico: "text-noc-accent",
  reserved: "text-noc-warn", installed: "text-noc-ok",
  defeito: "text-noc-err", lost: "text-noc-err", rma: "text-noc-warn",
};

export default function TenantInventoryTab({ tenantId }) {
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true); setErr(null);
    listInventory({ tenant_id: tenantId }, { signal: ac.signal })
      .then((d) => setData({ items: d.items || [], counts: d.counts || {} }))
      .catch((e) => { if (e?.name !== "AbortError") setErr(e?.message || "Falha ao carregar estoque."); })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [tenantId]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((i) =>
      [i.qr_serial, i.device_id, i.model, i.installer]
        .some((v) => (v || "").toLowerCase().includes(q)));
  }, [data.items, query]);

  if (loading) return <div className="text-sm text-noc-muted glass-card-pad">Carregando estoque…</div>;
  if (err) return <div className="text-sm text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg p-3">{err}</div>;
  if (data.items.length === 0) {
    return <div className="glass-card-pad text-sm text-noc-muted text-center py-10">
      Nenhuma unidade em estoque para esta empresa.
    </div>;
  }

  const counts = data.counts || {};
  const statusesWithCount = Object.keys(STATUS_LABEL).filter((s) => counts[s]);

  return (
    <div className="space-y-3">
      {/* contadores por status */}
      <div className="flex flex-wrap gap-2">
        <div className="glass-card-pad">
          <div className="text-2xl font-bold">{data.items.length}</div>
          <div className="text-xs text-noc-muted">Total</div>
        </div>
        {statusesWithCount.map((s) => (
          <div key={s} className="glass-card-pad">
            <div className={`text-2xl font-bold ${STATUS_TONE[s] || ""}`}>{counts[s]}</div>
            <div className="text-xs text-noc-muted">{STATUS_LABEL[s]}</div>
          </div>
        ))}
      </div>

      <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder="Buscar QR / device / modelo / técnico…"
        className="h-8 px-2.5 rounded-lg bg-noc-bg border border-noc-border text-xs text-noc-text w-full sm:w-72" />

      <div className="glass-card-pad overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-noc-muted text-xs border-b border-noc-border">
              <th className="py-2 pr-3">QR</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Modelo</th>
              <th className="py-2 pr-3">Device</th>
              <th className="py-2 pr-3">Técnico</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((i) => (
              <tr key={i.id || i.qr_serial} className="border-b border-noc-border/50">
                <td className="py-2 pr-3 font-mono text-xs">{i.qr_serial}</td>
                <td className={`py-2 pr-3 text-xs ${STATUS_TONE[i.status] || "text-noc-muted"}`}>
                  {STATUS_LABEL[i.status] || i.status}
                  {i.status_reason && <span className="text-noc-muted/60"> · {i.status_reason}</span>}
                </td>
                <td className="py-2 pr-3 text-noc-muted text-xs">{i.model || "—"}</td>
                <td className="py-2 pr-3 font-mono text-xs">{i.device_id || "—"}</td>
                <td className="py-2 pr-3 text-noc-muted text-xs font-mono">
                  {i.assigned_user_id ? i.assigned_user_id.slice(0, 8) : (i.installer || "—")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 && (
          <div className="text-xs text-noc-muted py-3 text-center">
            Nenhuma unidade para “{query.trim()}”.
          </div>
        )}
      </div>
    </div>
  );
}
